import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { claimRail, releaseRail } from '../lib/toastRail'

// ── Unified toast system ──────────────────────────────────────────────
// const toast = useToast()
// toast.show('Added to list')
// toast.show('Copied link', { duration: 1800 })
// toast.show('Removed', { tone: 'danger' })
// toast.show('Saved!', { tone: 'success', icon: '✓' })
// toast.show('Tonight, together →', { tone: 'amber', icon: '🍿' })

const ToastContext = createContext(null)

// Each tone carries an accent (icon chip + the receding time bar) and a glow
// (a colored bloom in the shadow) so the toast reads as premium, not flat.
// Shared TOAST_SHELL gradient formula (2026-08-15 audit): linear-gradient(135deg,
// {accent} 14%→#14141f, {accent} 6%→#0d0d16). Each tone's `accent` below feeds that
// formula so every toast in the app converges on one shell instead of 5 hand-rolled
// gradients. `eyebrow` is the tone's default label shown above the message — callers
// can still pass their own message text; this is only the default when none is split out.
const TONES = {
  default: { bg: 'linear-gradient(135deg, color-mix(in srgb, #fff 14%, #14141f), color-mix(in srgb, #fff 6%, #0d0d16))', border: 'rgba(var(--ink-rgb),0.14)', text: '#fff',     accent: 'rgba(var(--ink-rgb),0.6)', glow: 'rgba(var(--ink-rgb),0.05)', eyebrow: null },
  success: { bg: 'linear-gradient(135deg, color-mix(in srgb, var(--state-good) 14%, #14141f), color-mix(in srgb, var(--state-good) 6%, #0d0d16))', border: 'color-mix(in srgb, var(--state-good) 40%, transparent)',  text: '#a3f0c0', accent: 'var(--green)',               glow: 'color-mix(in srgb, var(--state-good) 20%, transparent)', eyebrow: 'Saved' },
  danger:  { bg: 'linear-gradient(135deg, color-mix(in srgb, var(--state-bad) 14%, #14141f), color-mix(in srgb, var(--state-bad) 6%, #0d0d16))', border: 'color-mix(in srgb, var(--state-bad) 42%, transparent)',  text: '#ffb0b5', accent: 'var(--state-bad)',               glow: 'color-mix(in srgb, var(--state-bad) 20%, transparent)', eyebrow: 'Error' },
  info:    { bg: 'linear-gradient(135deg, color-mix(in srgb, var(--accent) 14%, #14141f), color-mix(in srgb, var(--accent) 6%, #0d0d16))', border: 'color-mix(in srgb, var(--accent) 42%, transparent)',text: '#cfcaff', accent: 'var(--purple)',               glow: 'color-mix(in srgb, var(--accent) 20%, transparent)', eyebrow: 'Note' },
  amber:   { bg: 'linear-gradient(135deg, color-mix(in srgb, var(--amber) 14%, #14141f), color-mix(in srgb, var(--amber) 6%, #0d0d16))',  border: 'rgba(var(--amber-rgb),0.44)', text: '#ffe6a3', accent: 'var(--amber)',               glow: 'rgba(var(--amber-rgb),0.22)', eyebrow: null },
}

const OUT_MS = 260  // exit-animation length; leave fires this much before removal

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const show = useCallback((message, opts = {}) => {
    const id = Date.now() + Math.random()
    const duration = opts.duration ?? 2600
    // Cap the rail at 2 visible toasts and collapse an identical message (2026-09-14
    // fan-out sweep): a burst of the same "Saved" from rapid taps used to stack past
    // the nav. Newest wins; the oldest extra is dropped rather than queued.
    setToasts(t => {
      const rest = t.filter(x => x.message !== message)
      return [...rest.slice(-1), { id, message, tone: opts.tone || 'default', icon: opts.icon, duration }]
    })
    // `railClaim` (2026-09-17): a handful of these toasts announce the SAME action
    // UndoToast already confirms (the first-ever mark on a show) — claim the shared
    // lib/toastRail slot for as long as this one is visible so the two don't render
    // on top of each other (Jason: "clapboard, rating... mixed together"). Most
    // callers don't pass this and are unaffected.
    if (opts.railClaim) claimRail(opts.railClaim)
    if (duration > 0) {
      setTimeout(() => {
        setToasts(t => t.filter(x => x.id !== id))
        if (opts.railClaim) releaseRail(opts.railClaim)
      }, duration)
    }
    return id
  }, [])

  const dismiss = useCallback((id) => {
    setToasts(t => t.filter(x => x.id !== id))
  }, [])

  // Stable context value so useToast() consumers don't re-render every time a toast
  // shows/dismisses (show + dismiss are already stable useCallbacks).
  const ctxValue = useMemo(() => ({ show, dismiss }), [show, dismiss])

  return (
    <ToastContext.Provider value={ctxValue}>
      {children}
      {createPortal((
        // role=status + aria-live=polite so screen readers announce every toast —
        // the app's only success/error feedback channel (2026-07-06 a11y audit).
        <div role="status" aria-live="polite" aria-atomic="false" style={{
          position: 'fixed',
          bottom: 'calc(var(--nav-h) + 16px)',
          left: 0, right: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 8, zIndex: 'var(--z-share)', pointerEvents: 'none',
          padding: '0 16px',
        }}>
          {toasts.map(t => <Toast key={t.id} toast={t} onClose={() => dismiss(t.id)} />)}
        </div>
      ), document.body)}
    </ToastContext.Provider>
  )
}

function Toast({ toast, onClose }) {
  const [leaving, setLeaving] = useState(false)
  const tone = TONES[toast.tone] || TONES.default
  const duration = toast.duration ?? 2600

  // Play the exit animation just before the provider removes us — synced to the
  // toast's own duration (not a hardcoded constant), so custom durations exit cleanly.
  useEffect(() => {
    if (duration <= 0) return
    const t = setTimeout(() => setLeaving(true), Math.max(0, duration - OUT_MS))
    return () => clearTimeout(t)
  }, [duration])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.text,
        borderRadius:'var(--toast-radius)',
        padding: '12px 16px 13px',
        fontSize: 13.5, fontWeight: 500,
        fontFamily: 'var(--font)',
        boxShadow: `0 14px 38px rgba(0,0,0,0.55), 0 4px 18px ${tone.glow}, inset 0 1px 0 rgba(var(--ink-rgb),0.07)`,
        backdropFilter: 'blur(18px) saturate(1.4)',
        WebkitBackdropFilter: 'blur(18px) saturate(1.4)',
        pointerEvents: 'auto',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 10,
        '--nxa': leaving
          ? `toastOut ${OUT_MS}ms cubic-bezier(0.4, 0, 1, 1) forwards`
          : 'toastInBar 360ms cubic-bezier(0.22, 1.2, 0.36, 1) forwards',
        maxWidth: 480, width: 'fit-content',
      }}
    >
      {toast.icon && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 'var(--toast-chip-size)', height: 'var(--toast-chip-size)', borderRadius:'var(--radius-full)', flexShrink: 0,
          background: `${tone.accent}22`, border: `1px solid ${tone.accent}59`, color: tone.accent, fontSize: 'var(--toast-chip-icon-size)',
        }}>{toast.icon}</span>
      )}
      <span style={{ lineHeight: 1.35 }}>
        {tone.eyebrow && (
          <span style={{ display: 'block', color: tone.accent, fontSize: 'var(--toast-eyebrow-size)', fontWeight: 'var(--toast-eyebrow-weight)', letterSpacing: 'var(--toast-eyebrow-tracking)', textTransform: 'uppercase', marginBottom: 2 }}>
            {tone.eyebrow}
          </span>
        )}
        {toast.message}
      </span>

      {/* Receding time bar, empties left→right over the toast's lifetime, then
          fades as the toast exits. Clipped to the rounded corners by overflow:hidden. */}
      {duration > 0 && (
        <div style={{
          position: 'absolute', left: 0, bottom: 0, height: 2.5, width: '100%',
          background: `linear-gradient(90deg, ${tone.accent}, ${tone.accent}77)`,
          transformOrigin: 'left center',
          '--nxa': `toastBar ${duration}ms linear forwards`,
          opacity: leaving ? 0 : 0.95,
          transition: 'opacity 200ms ease-out',
        }}/>
      )}

      <style>{`
        @keyframes toastInBar  { 0% { opacity: 0; transform: translateY(22px) scale(0.92); filter: blur(3px); } 55% { filter: blur(0); } 100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); } }
        @keyframes toastOut { from { opacity: 1; transform: translateY(0) scale(1); } to { opacity: 0; transform: translateY(10px) scale(0.96); } }
        `}</style>
    </div>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  // Safe fallback if used outside provider (won't crash, just no-ops)
  if (!ctx) return { show: () => {}, dismiss: () => {} }
  return ctx
}
